import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BarChart3 } from "lucide-react";
import { getDefaultDateRange, DateRange } from "../../components/common/DateRangePicker";
import { STATUS_COLORS } from "../../components/dashboard/statusColors";
import {
  getDashboardStats,
  getOnboardingTrend,
  DashboardStats,
  OnboardingTrend,
  TrendGranularity,
} from "../../services/superAdmin.service";
import DoctorOverview from "../../components/dashboard/DoctorOverview";
import HospitalOverview from "../../components/dashboard/HospitalOverview";
import OnboardingLineChart from "../../components/dashboard/OnboardingLineChart";
import { handleApiError } from "../../utils/errorHandler";

// "YYYY-MM" -> "Sep '25"
const formatMonthLabel = (month: string): string => {
  const [year, m] = month.split("-");
  const date = new Date(Number(year), Number(m) - 1, 1);
  return `${date.toLocaleString("en-US", { month: "short" })} '${year.slice(2)}`;
};

const formatShortDate = (d: Date): string =>
  d.toLocaleString("en-US", { month: "short", day: "numeric" });

const parseBucketDate = (bucket: string): Date => new Date(`${bucket}T00:00:00`);

// Full period label shown in the point tooltip.
const formatBucketLabel = (bucket: string, granularity: TrendGranularity): string => {
  if (granularity === "month") return formatMonthLabel(bucket);
  const start = parseBucketDate(bucket);
  if (granularity === "week") {
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    return `${formatShortDate(start)} – ${formatShortDate(end)}`;
  }
  return `${formatShortDate(start)} '${String(start.getFullYear()).slice(2)}`;
};

// Sparse x-axis tick label for a bucket.
const formatXLabel = (bucket: string, granularity: TrendGranularity): string => {
  if (granularity === "month") return formatMonthLabel(bucket);
  return formatShortDate(parseBucketDate(bucket));
};

// ~4 evenly spaced x-axis tick labels across the buckets.
const buildXLabels = (buckets: string[], granularity: TrendGranularity): string[] => {
  const count = Math.min(4, buckets.length);
  if (count <= 1) return buckets.length ? [formatXLabel(buckets[0], granularity)] : [];
  return Array.from({ length: count }, (_, i) => {
    const index = Math.round((i / (count - 1)) * (buckets.length - 1));
    return formatXLabel(buckets[index], granularity);
  });
};

interface OnboardingChartCardProps {
  title: string;
  icon: ReactNode;
  color?: string;
  seriesKind: "hospitals" | "doctors";
}

const OnboardingChartCard = ({
  title,
  icon,
  color = STATUS_COLORS.active,
  seriesKind,
}: OnboardingChartCardProps) => {
  const defaultRange = useMemo(() => getDefaultDateRange(), []);
  const [dateRange, setDateRange] = useState<DateRange>(defaultRange);
  const [trend, setTrend] = useState<OnboardingTrend | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isStale, setIsStale] = useState<boolean>(false);

  // Keep track of active request to prevent duplicate/overlapping requests and race conditions
  const activeRequestIdRef = useRef<number>(0);

  const loadTrend = useCallback(async () => {
    const currentRequestId = ++activeRequestIdRef.current;
    setLoading(true);

    try {
      const result = await getOnboardingTrend({
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      });

      // Ignore response if another request was triggered in the meantime
      if (currentRequestId !== activeRequestIdRef.current) return;

      setTrend(result);
      setError(null);
      setIsStale(false);
    } catch (err: any) {
      if (currentRequestId !== activeRequestIdRef.current) return;
      const apiErr = handleApiError(err);
      setError(apiErr.message || "Failed to load onboarding statistics.");
      if (trend) {
        setIsStale(true);
      }
    } finally {
      if (currentRequestId === activeRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, [dateRange, trend]);

  useEffect(() => {
    loadTrend();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange.startDate, dateRange.endDate]);

  const points = useMemo(
    () =>
      trend
        ? trend[seriesKind].map((p) => ({
            value: p.count,
            label: formatBucketLabel(p.month, trend.granularity),
          }))
        : [],
    [trend, seriesKind]
  );

  const labels = useMemo(
    () =>
      buildXLabels(
        trend ? trend[seriesKind].map((p) => p.month) : [],
        trend?.granularity ?? "month"
      ),
    [trend, seriesKind]
  );

  return (
    <OnboardingLineChart
      title={title}
      icon={icon}
      points={points}
      xLabels={labels}
      color={color}
      startDate={dateRange.startDate}
      endDate={dateRange.endDate}
      onDateRangeChange={setDateRange}
      loading={loading}
      error={error}
      onRetry={loadTrend}
      isStale={isStale}
      onResetDateRange={() => setDateRange(defaultRange)}
      emptyActionTo={seriesKind === "hospitals" ? "/hospitals/new" : "/doctors/new"}
      emptyActionLabel={seriesKind === "hospitals" ? "Register Hospital" : "Add Doctor"}
    />
  );
};

const SuperAdminDashboard = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isStale, setIsStale] = useState<boolean>(false);

  // Request deduplication ref
  const activeStatsRequestRef = useRef<number>(0);

  const loadStats = useCallback(async () => {
    const requestId = ++activeStatsRequestRef.current;
    setLoading(true);

    try {
      const result = await getDashboardStats();
      if (requestId !== activeStatsRequestRef.current) return;
      setStats(result);
      setError(null);
      setIsStale(false);
    } catch (err: any) {
      if (requestId !== activeStatsRequestRef.current) return;
      const apiErr = handleApiError(err);
      setError(apiErr.message || "Failed to load dashboard statistics. Please try again.");
      if (stats) {
        setIsStale(true);
      }
    } finally {
      if (requestId === activeStatsRequestRef.current) {
        setLoading(false);
      }
    }
  }, [stats]);

  useEffect(() => {
    loadStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="space-y-8">
      {/* Top Section: Hospital Overview & Doctor Overview (Independent error isolation) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
        <HospitalOverview
          stats={stats ? stats.hospitals : null}
          loading={loading}
          error={error}
          onRetry={loadStats}
          isStale={isStale}
        />
        <DoctorOverview
          stats={stats ? stats.doctors : null}
          loading={loading}
          error={error}
          onRetry={loadStats}
          isStale={isStale}
        />
      </div>

      {/* Bottom Section: Hospital & Doctor Onboarding Trend Charts (Independently loaded) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <OnboardingChartCard
          title="Hospital Onboarding Statistics"
          icon={<BarChart3 size={16} aria-hidden="true" />}
          seriesKind="hospitals"
        />
        <OnboardingChartCard
          title="Doctor Onboarding Statistics"
          icon={<BarChart3 size={16} aria-hidden="true" />}
          seriesKind="doctors"
        />
      </div>
    </div>
  );
};

export default SuperAdminDashboard;
